import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import { px, REJECTION_COLORS } from '../utils/dynamanSimulation';

/**
 * THE SIGNAL AUDIT MATRIX: every structural trigger that fired and was turned down.
 *
 * <h2>This is the answer to "why did it only take four trades"</h2>
 *
 * Before the audit existed, a run that declined a thousand setups and a run where nothing ever
 * triggered produced the identical artefact: an empty ledger. The counts along the top are the
 * whole point — on a real SPY run they read 945 blocked by an open position against 11 blocked by
 * the macro trend, which says something completely different about the rules than the trade list
 * does on its own.
 *
 * <h2>Filtering is by reason CODE, not by label</h2>
 *
 * The chips filter on the enum name the server sends alongside the display text. Grouping on the
 * text would make a copy edit silently reshape the matrix.
 *
 * <h2>Rows are capped, and the cap is stated</h2>
 *
 * A long history declines thousands of signals and rendering them all locks the browser. The table
 * shows the most recent {@link ROW_CAP} and says how many it is not showing — a silent truncation
 * would read as "that is all of them", which is the same class of mistake the audit exists to fix.
 */

export const ROW_CAP = 500;

const stamp = (iso) => (iso ? String(iso).replace('T', ' ').slice(0, 16) : '—');

const DynaManAuditTable = ({ rejections, rejectionCounts = null }) => {
  const [active, setActive] = useState(null);

  const counts = useMemo(() => {
    if (rejectionCounts && Object.keys(rejectionCounts).length) return rejectionCounts;
    return rejections.reduce((acc, r) => {
      acc[r.reasonCode] = (acc[r.reasonCode] || 0) + 1;
      return acc;
    }, {});
  }, [rejections, rejectionCounts]);

  const filtered = useMemo(
    () => (active ? rejections.filter((r) => r.reasonCode === active) : rejections),
    [rejections, active]
  );

  // The most recent are the interesting ones — a strategy's recent refusals are the ones you can
  // still check against a chart.
  const visible = useMemo(() => filtered.slice(-ROW_CAP).reverse(), [filtered]);
  const hidden = filtered.length - visible.length;

  const labelFor = (code) => rejections.find((r) => r.reasonCode === code)?.reason || code;

  if (!rejections.length) {
    return (
      <Alert severity="success">
        No structural trigger was declined in this run. Every breakout that fired was either taken
        or never occurred.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {rejections.length} structural triggers fired and were declined
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={`All (${rejections.length})`}
          color={active === null ? 'primary' : 'default'}
          variant={active === null ? 'filled' : 'outlined'}
          onClick={() => setActive(null)}
          size="small"
        />
        {Object.entries(counts).map(([code, count]) => (
          <Chip
            key={code}
            label={`${labelFor(code)} (${count})`}
            color={active === code ? (REJECTION_COLORS[code] || 'default') : 'default'}
            variant={active === code ? 'filled' : 'outlined'}
            onClick={() => setActive(active === code ? null : code)}
            size="small"
          />
        ))}
      </Stack>

      {hidden > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing the most recent {visible.length}. {hidden} older rows are not rendered — filter by
          a reason above, or export the run to see them all.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['When', 'TF', 'Close', 'Volume', 'State at signal', 'Declined by'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r, i) => (
              <TableRow key={`${r.time}-${r.reasonCode}-${i}`} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{stamp(r.time)}</TableCell>
                <TableCell>{r.timeframe}</TableCell>
                <TableCell align="right">{px(r.close)}</TableCell>
                <TableCell align="right">{r.volume?.toLocaleString?.() ?? r.volume}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={r.state} />
                </TableCell>
                <TableCell>
                  <Tooltip title={r.detail || ''} arrow>
                    <Chip
                      size="small"
                      label={r.reason}
                      color={REJECTION_COLORS[r.reasonCode] || 'default'}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

DynaManAuditTable.propTypes = {
  rejections: PropTypes.arrayOf(PropTypes.shape({
    time: PropTypes.string,
    timeframe: PropTypes.string,
    close: PropTypes.number,
    volume: PropTypes.number,
    state: PropTypes.string,
    reasonCode: PropTypes.string,
    reason: PropTypes.string,
    detail: PropTypes.string,
  })).isRequired,
  rejectionCounts: PropTypes.objectOf(PropTypes.number),
};

export default DynaManAuditTable;
